import React, { useState } from 'react';
import { getAccessCodes, createAccessCode } from '../utils/accessCodeApi';

const AccessCodeTester = () => {
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newAccessCode, setNewAccessCode] = useState({
    value: '',
    code: '',
    quantity: '',
    status: 'active'
  });

  // Test getting access codes list
  const testGetAccessCodes = async () => {
    setLoading(true);
    setMessage('Fetching access codes...');
    
    try {
      const response = await getAccessCodes();
      
      if (response.success) {
        let data = response.data;
        
        // Handle different API response structures
        if (response.data && response.data.data) {
          data = response.data.data;
        } else if (response.data && response.data.access_codes) {
          data = response.data.access_codes;
        } else if (response.data && response.data.items) {
          data = response.data.items;
        }
        
        if (Array.isArray(data)) {
          setAccessCodes(data);
          setMessage(`✅ Successfully fetched ${data.length} access codes from API`);
        } else {
          setAccessCodes([]);
          setMessage('⚠️ API returned data but it\'s not an array');
        }
      } else {
        setAccessCodes([]);
        setMessage(`❌ API Error: ${response.error}`);
      }
    } catch (error) {
      setAccessCodes([]);
      setMessage(`❌ Network Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test creating a new access code
  const testCreateAccessCode = async () => {
    if (!newAccessCode.value || !newAccessCode.code || !newAccessCode.quantity) {
      setMessage('❌ Please fill in all required fields');
      return;
    }

    setLoading(true);
    setMessage('Creating new access code...');
    
    try {
      const response = await createAccessCode(newAccessCode);
      
      if (response.success) {
        setMessage('✅ Successfully created new access code');
        setNewAccessCode({ value: '', code: '', quantity: '', status: 'active' });
        // Refresh the list
        testGetAccessCodes();
      } else {
        setMessage(`❌ API Error: ${response.error}`);
      }
    } catch (error) {
      setMessage(`❌ Network Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-900">Access Code API Tester</h2>
      
      {/* Status Message */}
      {message && (
        <div className={`p-4 rounded-md mb-6 ${
          message.includes('✅') ? 'bg-green-100 text-green-800' :
          message.includes('⚠️') ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Test Get Access Codes */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">1. Test Getting Access Codes List</h3>
        <button
          onClick={testGetAccessCodes}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Get Access Codes from API'}
        </button>
      </div>

      {/* Test Create Access Code */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">2. Test Creating New Access Code</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Value *
            </label>
            <input
              type="text"
              value={newAccessCode.value}
              onChange={(e) => setNewAccessCode({...newAccessCode, value: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Premium Access"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code *
            </label>
            <input
              type="text"
              value={newAccessCode.code}
              onChange={(e) => setNewAccessCode({...newAccessCode, code: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., AC007"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              value={newAccessCode.quantity}
              onChange={(e) => setNewAccessCode({...newAccessCode, quantity: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={newAccessCode.status}
              onChange={(e) => setNewAccessCode({...newAccessCode, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
        <button
          onClick={testCreateAccessCode}
          disabled={loading}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Access Code'}
        </button>
      </div>

      {/* Display Results */}
      {accessCodes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Access Codes from API</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {accessCodes.map((accessCode) => (
                  <tr key={accessCode.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{accessCode.id}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{accessCode.value}</td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-900">{accessCode.code}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{accessCode.quantity}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        accessCode.status === 'active' ? 'bg-green-100 text-green-800' :
                        accessCode.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        accessCode.status === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {accessCode.status}
                      </span>
                    </td>
                    
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* API Information */}
      <div className="mt-8 p-4 bg-gray-50 rounded-md">
        <h4 className="font-semibold text-gray-900 mb-2">API Information</h4>
        <p className="text-sm text-gray-600 mb-2">
          <strong>Base URL:</strong> http://127.0.0.1:8000/api
        </p>
        <p className="text-sm text-gray-600 mb-2">
          <strong>Get Access Codes:</strong> GET /api/access-codes
        </p>
        <p className="text-sm text-gray-600 mb-2">
          <strong>Create Access Code:</strong> POST /api/access-codes
        </p>
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> If the API is not available, the main Access Codes page will show mock data.
        </p>
      </div>
    </div>
  );
};

export default AccessCodeTester;
